import classNames from 'classnames'
import Link from 'next/link'
import { ProductVariantValue } from './product-variants'
import { ProductForPageFragment } from '@productsite-beta/src/graphql/generated/graphql-types'
import { hasNoValidVariantCombinations, isNotAvailableForSale } from './product-variant-helper'
import ProductVariantPrice from './product-variant-price'
import { getPrice } from '@dustin-web/microsite-data/src/utils'
import {
  useTranslations,
  useUserSettings,
} from '@dustin-web/microsite-components/src/hooks'
import SnackBar from '@dustin-web/microsite-components/src/snack-bar/snack-bar'
import { useState } from 'react'
import { getProductUrl } from '@dustin-web/microsite-components/src/utils/urls'
import { ProductDigitalData } from '@dustin-web/product-components/src/utils/product-digital-data'
import router from 'next/router'

const VARIANT_VALUE_TEXT_LENGTH_LIMIT = 10
const DONT_SHOW_AGAIN_LOCALSTORAGE_KEY = 'dustin.modal.variants'

type Props = {
  variantName: string
  variantNameEng?: string | null
  variantValues: ProductVariantValue[]
  mainProduct: ProductForPageFragment
  onClick: (arg0: string[], arg1: string) => void
}

export default function VariantSections({
  variantValues,
  variantName,
  variantNameEng,
  mainProduct,
  onClick,
}: Props) {
  const [showSnackBar, setShowSnackBar] = useState(false)
  const { translate } = useTranslations()
  const { withoutText, showPricesInclVat } = useInitialVariantConfig()

  const { showPrice, formattedVariantOptions } = getValues({
    variantValues,
    mainProduct,
    variantName,
    withoutText,
    showPricesInclVat,
    variantNameEng,
  })
  const isVariantAvailableForSale = formattedVariantOptions.filter(isAvailableForSale)
  const showVariantButtons = isVariantAvailableForSale.length > 1
  const ownVariant = formattedVariantOptions.find(v => isOwnVariant(v, mainProduct))
  const ownVariantValueText = getOwnVariantValueText(ownVariant, withoutText, variantName) ?? ''
  const oneOfItemsHasLongName = formattedVariantOptions.some(item => item.isLongDisplayName)

  return (
    <>
      <VariantHeading
        showVariantButtons={showVariantButtons}
        variantName={variantName}
        ownVariantValueText={ownVariantValueText}
      />
      <VariantList
        list={formattedVariantOptions}
        showPrice={showPrice}
        onClick={onClick}
        oneOfItemsHasLongName={oneOfItemsHasLongName}
        variantNameEng={variantNameEng}
        ownVariantValueText={ownVariantValueText}
      />

      <SnackBar
        open={showSnackBar}
        onToggle={() => setShowSnackBar(!showSnackBar)}
        message={translate('Variant_Selections_Has_Changed')}
        closeButton
        icon="info"
        iconClassName="i-20 u-mr-0"
        className="t-dark-purple t-nullify u-color-light-purple"
      />
    </>
  )
}

type VariantHeadingProps = {
  showVariantButtons: boolean
  variantName: string
  ownVariantValueText: string
}
function VariantHeading({
  showVariantButtons,
  variantName,
  ownVariantValueText,
}: VariantHeadingProps) {
  return (
    <h2
      className={classNames('c-product-variant__heading', {
        'c-product-variant__heading--no-btns': !showVariantButtons,
      })}>
      <strong>{variantName}:</strong> {ownVariantValueText}
    </h2>
  )
}
function VariantList({
  list,
  showPrice,
  onClick,
  oneOfItemsHasLongName,
  variantNameEng,
  ownVariantValueText,
}: {
  list: ExtentedVariantOption[]
  showPrice: boolean
  oneOfItemsHasLongName: boolean
  onClick: Props['onClick']
  variantNameEng: Props['variantNameEng']
  ownVariantValueText: string
}) {
  return (
    <div className="c-product-variant__list-container">
      {list.map((v, key) => (
        <Item
          key={key}
          option={v}
          onClick={onClick}
          oneOfItemsHasLongName={oneOfItemsHasLongName}
          variantNameEng={variantNameEng}
          ownVariantValueText={ownVariantValueText}>
          <Price price={v.formattedPrice} hide={v.hasNoAvailableVariants || !showPrice} />
        </Item>
      ))}
    </div>
  )
}

function Price({ hide, price }: { hide: boolean; price: any }) {
  if (hide) {
    return null
  }
  return <ProductVariantPrice price={price} />
}
type ItemProps = {
  option: ExtentedVariantOption
  children: React.ReactNode
  onClick: Props['onClick']
  oneOfItemsHasLongName: boolean
  variantNameEng: Props['variantNameEng']
  ownVariantValueText: string
}
function Item({
  option,
  children,
  onClick,
  oneOfItemsHasLongName,
  variantNameEng,
  ownVariantValueText,
}: ItemProps) {
  const variantClasses = classNames({
    'product-variant-text-boxes-container-big': oneOfItemsHasLongName,
    'product-variant-text-boxes-container': !oneOfItemsHasLongName,
  })
  const linkClasses = classNames('c-product-variant c-product-variant--w-text', {
    'c-product-variant--selected--with-color': option.selected,
    'c-product-variant--not-available': option.hasNoAvailableVariants,
    'u-disabled': option.isNotAvaialbleForSale,
  })
  const productHref = getProductUrl(
    option.productErpIdentifier || '',
    option.product?.nameSlug || ''
  )

  const tracking = (variantValue?: string | null) => {
    if (!variantValue || !ownVariantValueText) return
    ProductDigitalData.pushSelectedVariant('button_click', variantValue, ownVariantValueText)
  }

  function handleOnVariantClicked() {
    tracking(`${variantNameEng} ${option.displayName}`.trim())
  }

  function handleOnUnavailableClicked(productVariantValue: any, e: React.MouseEvent) {
    e.preventDefault()
    if (localStorage.getItem(DONT_SHOW_AGAIN_LOCALSTORAGE_KEY) !== 'true') {
      onClick(
        productVariantValue.missingVariantNames,
        getProductUrl(
          productVariantValue.productErpIdentifier || '',
          productVariantValue.product?.nameSlug || ''
        )
      )
    } else {
      router.push(
        getProductUrl(
          productVariantValue.productErpIdentifier || '',
          productVariantValue.product?.nameSlug || ''
        )
      )
    }
  }

  const displayName = option.hasNoAvailableVariants ? `*${option.displayName}` : option.displayName
  const elementProps = option.hasNoAvailableVariants && {
    onClick: (e: React.MouseEvent) => handleOnUnavailableClicked(option, e),
  }

  {
    return (
      <li className={variantClasses} style={{ listStyle: 'none' }} onClick={handleOnVariantClicked}>
        <Link
          {...elementProps}
          scroll={false}
          href={productHref}
          className={linkClasses}
          aria-label={ownVariantValueText}
          style={{ marginBottom: '0 !important' }}>
          <strong className="u-fs-14">{displayName}</strong>
          {children}
        </Link>
      </li>
    )
  }
}

type FormattedVariantOption = {
  displayName: string
  isLongDisplayName: boolean
  selected: boolean
  isNotAvaialbleForSale: boolean
  formattedPrice: string
  hasNoAvailableVariants: boolean
}
type ExtentedVariantOption = FormattedVariantOption & ProductVariantValue

type FormatOptionsParams = {
  options: ProductVariantValue[]
  variantName: Props['variantName']
  withoutText: string
  currentProductId: string | undefined
  isMainAvailableForSale: boolean | undefined
  showPricesInclVat: boolean
  variantNameEng: Props['variantNameEng']
}
function getFormattedVariantOptions({
  options,
  variantName,
  withoutText,
  currentProductId,
  isMainAvailableForSale,
  showPricesInclVat,
  variantNameEng,
}: FormatOptionsParams): ExtentedVariantOption[] {
  const formatted = options.map(option => {
    const displayName = getVariantDisplayName(option, withoutText, variantName).trim()
    const price = option?.product?.price
    return {
      ...option,
      displayName,
      isLongDisplayName: displayName.length > VARIANT_VALUE_TEXT_LENGTH_LIMIT,
      selected: option?.productErpIdentifier === currentProductId,
      hasNoAvailableVariants: getHasNoAvailableVariants(isMainAvailableForSale, option),
      isNotAvaialbleForSale: isNotAvailableForSale(option),
      formattedPrice: price ? getPrice(price, showPricesInclVat) : 'no price found', //TODO: fix showInclVat
      variantNameEng,
    }
  })
  return formatted as ExtentedVariantOption[] //TODO: fix this as typing
}
function getVariantDisplayName(
  option: ProductVariantValue,
  withoutText: string,
  variantName: string
) {
  return `${option?.itemValueText || `${withoutText} ${variantName.toLocaleLowerCase()}`} ${option?.unitText ?? ''
    }`
}

function diffExcludedVat(variant: ProductVariantValue, ownVariant: ProductVariantValue) {
  return (
    variant?.product?.price?.priceExcludingVat !== ownVariant?.product?.price?.priceExcludingVat
  )
}

function getHasNoAvailableVariants(
  isMainAvailableForSale: boolean | undefined,
  option: ProductVariantValue
) {
  return (
    isMainAvailableForSale &&
    (hasNoValidVariantCombinations(option) || isNotAvailableForSale(option))
  )
}
function useInitialVariantConfig() {
  const { translate } = useTranslations()
  const { showPricesInclVat } = useUserSettings()
  const withoutText = translate('Variant_Without')

  return {
    showPricesInclVat,
    withoutText,
  }
}
type getValuesParams = {
  variantValues: Props['variantValues']
  mainProduct: Props['mainProduct']
  variantName: Props['variantName']
  withoutText: string
  showPricesInclVat: boolean
  variantNameEng: Props['variantNameEng']
}
function getValues({
  variantValues,
  mainProduct,
  variantName,
  withoutText,
  showPricesInclVat,
  variantNameEng,
}: getValuesParams) {
  const availableVariants = variantValues.filter(
    (v: ProductVariantValue) => v && v.product?.availability?.isAvailableForSale
  )
  // If the chosen variant is hidden we should display all of the variants
  const currentVariantIndex = availableVariants.findIndex(
    x => x?.productErpIdentifier === mainProduct.productErpIdentifier
  )

  const formattedVariantOptions = getFormattedVariantOptions({
    options: availableVariants,
    variantName,
    withoutText,
    currentProductId: mainProduct.productErpIdentifier,
    isMainAvailableForSale: mainProduct.availability?.isAvailableForSale,
    showPricesInclVat,
    variantNameEng,
  })

  const selectedVariantOption = formattedVariantOptions.find(
    v => v?.productErpIdentifier === mainProduct.productErpIdentifier
  )
  const showPrice = formattedVariantOptions
    .filter(
      (variant: ExtentedVariantOption) =>
        !hasNoValidVariantCombinations(variant) && !isNotAvailableForSale(variant)
    )
    .some((v: ExtentedVariantOption) => diffExcludedVat(v, selectedVariantOption))

  return {
    showPrice,
    formattedVariantOptions,
    currentVariantIndex,
  }
}
function isAvailableForSale(variant: any) {
  return variant?.product?.availability?.isAvailableForSale
}
function getOwnVariantValueText(
  ownVariant: any,
  withoutText: string,
  variantName: Props['variantName']
) {
  return ownVariant?.itemValueText
    ? `${ownVariant?.itemValueText} ${ownVariant?.unitText ? ownVariant?.unitText : ''}`.trim()
    : `${withoutText} ${variantName.toLocaleLowerCase()}`.trim()
}
function isOwnVariant(v: any, mainProduct: any) {
  return v?.productErpIdentifier === mainProduct?.productErpIdentifier
}
